import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { BunkerPlayerPublic } from '@shum/shared';
import { QrCode } from '../components/QrCode';
import { BunkerStyles } from '../game/bunker/styles';
import { formatRemaining, useCountdown } from '../lib/format';
import { useBunkerSocket } from '../lib/bunkerSocket';

/**
 * Public-only projection for a TV or projector. It deliberately never sends a
 * reconnect token, so this tab cannot receive a player's private character.
 */
export function BunkerSharedScreen() {
  const { code } = useParams<{ code: string }>();
  const normalizedCode = code?.toUpperCase();
  const { state } = useBunkerSocket(normalizedCode, { publicOnly: true });
  const publicState = state.publicState;
  const joinUrl = useMemo(() => `${window.location.origin}/b/${normalizedCode ?? ''}`, [normalizedCode]);
  const seconds = useCountdown(publicState?.endsAt ?? null);

  return (
    <div className="bk-root bk-shared-root">
      <BunkerStyles />
      <SharedStyles />
      <header className="bk-shared-header">
        <Link className="bk-brand" to="/"><span className="bk-brand-mark">B</span><span>Бункер · общий экран</span></Link>
        <div className="bk-shared-code"><span>Код комнаты</span><strong>{normalizedCode ?? '—'}</strong></div>
      </header>

      {!publicState ? (
        <main className="bk-stage"><span className="bk-eyebrow">Общий экран</span><h1>Подключаемся к комнате…</h1><p>{state.error?.message ?? 'Здесь появятся только открытые данные матча.'}</p></main>
      ) : (
        <main className="bk-shared-main">
          <section className="bk-shared-hero">
            <div>
              <span className="bk-eyebrow">Раунд {publicState.roundNumber || '—'} · {phaseLabel(publicState.phase)}</span>
              <h1>{publicState.scenario?.title ?? 'Ожидание выживших'}</h1>
              <p>{publicState.scenario?.shortDescription ?? 'Отсканируйте QR-код, выберите имя и подтвердите готовность.'}</p>
            </div>
            <div className="bk-shared-timer" aria-label="Осталось времени">{publicState.endsAt ? formatRemaining(seconds) : '—'}</div>
          </section>

          <section className="bk-shared-info">
            <article><span>Бункер</span><strong>{publicState.bunker?.title ?? 'Выбирается системой'}</strong><small>{publicState.bunker ? `${publicState.bunker.area} · автономность ${publicState.bunker.autonomy}` : 'Параметры появятся при старте'}</small></article>
            <article><span>Места</span><strong>{publicState.capacity ?? 'авто'}</strong><small>активных игроков: {publicState.players.filter((player) => player.isActive).length}</small></article>
            <article className="bk-shared-qr"><QrCode value={joinUrl} size={92} /><small>Сканируйте, чтобы войти</small></article>
          </section>

          {publicState.bunker && <div className="bk-feature-strip">{publicState.bunker.revealedFeatures.map((feature) => <span key={feature}>{feature}</span>)}</div>}
          <section className="bk-shared-section">
            <div className="bk-section-head inline"><div><span className="bk-eyebrow">Открытые карточки</span><h2>Выжившие</h2></div><span className="bk-shared-count">{publicState.players.length} в комнате</span></div>
            <div className="bk-player-wall bk-shared-player-wall">{publicState.players.map((player) => <PublicPlayer key={player.playerId} player={player} />)}</div>
          </section>
          <section className="bk-shared-log"><span className="bk-eyebrow">Последние события</span>{publicState.logs.slice().reverse().map((entry) => <p key={entry.id}>{entry.text}</p>)}</section>
        </main>
      )}
    </div>
  );
}

function PublicPlayer({ player }: { player: BunkerPlayerPublic }) {
  return <article className={`bk-public-player ${player.isEliminated ? 'eliminated' : ''}`}><div className="bk-player-name"><span>{player.avatarId}</span><strong>{player.name}</strong>{player.isCreator && <em>ведущий</em>}</div><div className="bk-public-fields">{player.revealedFields.length ? player.revealedFields.map((field) => <span key={field.type}><b>{field.label}</b> {field.value}</span>) : <span className="unknown">характеристики скрыты</span>}</div>{player.hasImmunity && <small className="bk-effect">иммунитет</small>}</article>;
}

function phaseLabel(phase: string): string {
  const labels: Record<string, string> = { LOBBY: 'лобби', INTRO: 'катастрофа', CHARACTER_REVIEW: 'знакомство', ROUND_START: 'новый раунд', REVEAL_SELECTION: 'выбор характеристики', PLAYER_REVEAL: 'раскрытие', PLAYER_SPEECH: 'выступление', GENERAL_DISCUSSION: 'обсуждение', ACTION_WINDOW: 'карты', VOTING: 'тайное голосование', VOTE_RESULTS: 'результаты', TIE_DISCUSSION: 'ничья', REVOTE: 'переголосование', ELIMINATION: 'исключение', RESULTS: 'итоги', PAUSED: 'пауза' };
  return labels[phase] ?? phase;
}

function SharedStyles() {
  return <style>{`
    .bk-shared-root { min-width: 1024px; padding: 22px 32px 42px; background: radial-gradient(circle at 83% 8%, color-mix(in srgb, #d86f34 17%, transparent), transparent 28%), var(--bg); }
    .bk-shared-header { max-width: 1480px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; padding-bottom: 18px; border-bottom: 1px solid var(--line); }
    .bk-shared-code { display: flex; align-items: baseline; gap: 11px; color: var(--ink-faint); font: 10px 'JetBrains Mono', monospace; letter-spacing: .1em; text-transform: uppercase; }
    .bk-shared-code strong { color: #d86f34; font-size: 23px; letter-spacing: .16em; }
    .bk-shared-main { max-width: 1480px; margin: 0 auto; padding-top: 28px; }
    .bk-shared-hero { display: grid; grid-template-columns: 1fr auto; align-items: end; gap: 26px; padding: 8px 0 26px; }
    .bk-shared-hero h1 { margin: 7px 0 10px; font: 600 clamp(50px, 5.2vw, 88px)/.92 'Oswald', sans-serif; letter-spacing: -.03em; }
    .bk-shared-hero p { max-width: 760px; margin: 0; color: var(--ink-dim); font-size: 16px; line-height: 1.5; }
    .bk-shared-timer { color: #d86f34; font: 600 clamp(54px, 6vw, 94px)/.9 'JetBrains Mono', monospace; letter-spacing: -.08em; font-variant-numeric: tabular-nums; }
    .bk-shared-info { display: grid; grid-template-columns: 1fr .65fr auto; gap: 12px; }
    .bk-shared-info article { min-height: 122px; padding: 18px; border: 1px solid var(--line); border-radius: 12px; background: var(--bg2); display: flex; flex-direction: column; justify-content: center; }
    .bk-shared-info span, .bk-shared-info small { color: var(--ink-faint); font: 10px 'JetBrains Mono', monospace; letter-spacing: .1em; text-transform: uppercase; }
    .bk-shared-info strong { margin: 7px 0; font: 500 31px 'Oswald', sans-serif; }
    .bk-shared-qr { flex-direction: row !important; align-items: center; gap: 13px; padding: 10px !important; }
    .bk-shared-qr small { width: 90px; line-height: 1.45; }
    .bk-shared-section { margin-top: 25px; }
    .bk-shared-section h2 { margin: 5px 0 0; font: 500 36px 'Oswald', sans-serif; }
    .bk-shared-count { color: var(--ink-faint); font: 11px 'JetBrains Mono', monospace; letter-spacing: .08em; text-transform: uppercase; }
    .bk-shared-player-wall { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    .bk-shared-log { display: grid; grid-template-columns: 170px 1fr 1fr; gap: 10px 18px; margin-top: 24px; padding-top: 18px; border-top: 1px solid var(--line); }
    .bk-shared-log > span { grid-row: span 4; }
    .bk-shared-log p { margin: 0; color: var(--ink-dim); font-size: 13px; line-height: 1.4; }
  `}</style>;
}
